import Fuse from 'fuse.js';
import { useCallback, useRef } from 'react';
import { useImmer } from 'use-immer';
import api, { RequestMethod } from '../api';
import { useRequest, useRequestImmer } from '../api/hooks';
import { APIError } from '../api/types';
import { fuseGoodsOptions, FuseGoodsType, goodsToSearchDoc, SearchGoodsFuncType } from '@/utils/search';
import { EditorInfo, GMTInfo, Page } from './common';
import { ID } from '../global';

export const skuEndpoint = `/goods`;
export const StandardProductEndpoint = `/standardProduct`;
export const SpecificationEndpoint = `/specification`;

export enum GOODS_TYPE {
  /**普通实物商品 */
  NORMAL = 100,

  /**时长性服务 */
  SERVICE = 101,

  /**预付卡 */
  CARD = 500,
}

export const getGoodsTypeDesc = (type: GOODS_TYPE) => {
  switch (type) {
    case GOODS_TYPE.NORMAL:
      return '实物';
    case GOODS_TYPE.SERVICE:
      return '服务';
    case GOODS_TYPE.CARD:
      return '预付卡';
  }
};

export interface StandardProduct {
  id: number;
  barcode: string;
  productName: string;
  productEnName: string;
  brandName: string;
  gallery: string[];
  originGallery: string[];
  type: string;
  width: string;
  height: string;
  depth: string;
  keyword: string;
  description: string;
  manufacturerName: string;
  manufacturerAddress: string;
  manufacturerFirmCode: string;
  categoryCode: string;
  origin: string;
  memo: string;
  checkResult: string;
  price: number;
}

/** SKU */
export interface SKU extends GMTInfo, EditorInfo {
  id: number;

  /**自编号 */
  goodsSn: string;

  /**商品类型 */
  type: GOODS_TYPE;

  /** sku 名 */
  name: string;

  /** sku 名 拼音 */
  namePy: string;

  /**商品主图 */
  picUrl: string;

  /**minorImgs */
  gallery: string[];

  /**广告语 */
  ad: string;

  /**服务时长 */
  duration: number;

  /**上架 */
  isOnSale: boolean;

  isStoreOnSale: boolean;

  /**价格 */
  price: number;

  /**划线价 */
  originalPrice: number;

  /**可见性 */
  visible: boolean;

  /** sku 详情 */
  detail: string;

  /** sku 简介 */
  brief: string;

  /**分类 id */
  categoryId: number;

  /**品牌 id */
  brandId: number;

  /**展示优先级 */
  sortOrder: number;

  /**企业 id */
  eid: number;

  /**标准化商品库 id */
  spId: number;

  /**二维码 */
  qrUrl: string;

  /**分享图 5:4 */
  shareUrl: string;
}

export interface SKURelated {
  id: number;
  specificationId: number;
  relatedSpecificationId: number;
  note?: string;
  sortOrder?: number;
}

export interface SKUAttribute extends GMTInfo {
  id?: number;
  goodsId?: number;
  attribute: string;
  value: string;
}

export interface SKUSpecification extends GMTInfo {
  id: number;
  goodsId: number;
  goodsSn?: string;
  goodsName?: string;
  goodsType?: SKU['type'];
  goodsPicUrl?: string;
  goodsDuration?: SKU['duration'];
  categoryId?: number;
  isStoreOnSale?: boolean;
  name: string;
  desc: string;
  picUrl: string;
  price: number;
  stock: number;
  /** 用于购物清单 */
  number: number;
  /** 成本价 */
  cost: number;
}

export interface Specification extends GMTInfo {
  id: number;
  goodsId: number;
  name: string;
  desc: string;
  picUrl: string;
  price: number;
  cost: number;
  stock: number;
  sortOrder?: number;
}

export interface SKUWrapper {
  goods: SKU;
  // categoryIds: number[];
  attributes?: SKUAttribute[];
  specifications?: Specification[];
}

export const useFetchSkuRelatedList = () =>
  useRequestImmer<{
    list: SKURelated[];
    page: Page;
    specificationMap: { [specificationId: number]: SKUSpecification };
  }>(
    {
      method: 'get',
      url: `${skuEndpoint}/related`,
    },
    {
      manual: true,
    }
  );

export const useAddOrUpdateSkuRelated = () =>
  useRequestImmer<SKURelated>(
    {
      method: 'post',
      url: `${skuEndpoint}/related`,
    },
    {
      manual: true,
    }
  );
export const deleteSkuRelated = (id: ID) =>
  api.delete({
    url: `${skuEndpoint}/related/${id}`,
  });

/**
 * 根据规格 id 查询规格列表
 * @returns
 */
export const useRequestSpecificationList = () => {
  const { send, ...others } = useRequest<SKUSpecification[]>(
    {
      method: 'post',
      url: `${SpecificationEndpoint}`,
    },
    {
      manual: true,
    }
  );

  const post = (ids: ID[]) => {
    return send({
      data: ids,
    });
  };

  return {
    send: post,
    ...others,
  };
};

export const useAddOrUpdateSku = () => {
  const { send, ...others } = useRequest<SKUWrapper>(
    {
      method: 'get',
      url: `${skuEndpoint}`,
    },
    {
      manual: true,
    }
  );

  const save = (skuWrapper: SKUWrapper) => {
    let requestMethod: RequestMethod = skuWrapper.goods.id ? 'put' : 'post';
    return send({
      method: requestMethod,
      data: skuWrapper,
    });
  };

  return {
    send: save,
    ...others,
  };
};

/**
 * 入库 / 出库
 * @param operation
 * @param specificationId
 * @param goodsId
 * @param alterNum
 */
export const alterStock = (operation: 'add' | 'reduce', specificationId: number, goodsId: number, alterNum: number) =>
  api.put<number>({
    url: `${SpecificationEndpoint}/stock`,
    data: {
      operation,
      id: specificationId,
      goodsId,
      number: alterNum,
    },
  });

export const requestAllGoods = () =>
  api.get<SKU[]>({
    url: `${skuEndpoint}/getAll`,
  });

export const requestAllSpecifications = () =>
  api.get<SKUSpecification[]>({
    url: `${SpecificationEndpoint}/getAll`,
  });

export const requestHasSkuSn = (sn: string) =>
  api.get<boolean>({
    url: `${skuEndpoint}/hasSn/${sn}`,
  });

export const useStandardProductList = () => {
  return useRequestImmer<{ list: StandardProduct[]; page: Page }>(
    {
      url: `${StandardProductEndpoint}/`,
    },
    { manual: true }
  );
};

export const useRequestEditStandardProduct = () =>
  useRequest<StandardProduct>(
    {
      url: `${StandardProductEndpoint}/`,
      method: 'post',
    },
    { manual: true }
  );

export const useGetGoodsWithSpecificationsBySnOrId = () =>
  useRequestImmer<SKUWrapper>(
    {
      method: 'get',
      url: `${skuEndpoint}/snOrId`,
    },
    {
      manual: true,
    }
  );

// export const useGetAllWithSpecifications = () =>
//   useRequestImmer<InventoryItem[]>(
//     {
//       method: 'get',
//       url: `${skuEndpoint}/allWithSpecifications`,
//     },
//     {
//       manual: true,
//     }
//   );

export const useAllGoods = () => {
  const goodsSearchIndex = useRef(new Fuse<FuseGoodsType>([], fuseGoodsOptions));

  // let hasSetSearchIndex = useRef<boolean>(false);

  const searchGoods: SearchGoodsFuncType = useCallback(
    (...args) => {
      return goodsSearchIndex.current.search(...args);
    },
    [goodsSearchIndex]
  );

  // 全部商品
  const [goodsMap, setGoodsMap] = useImmer<Map<number, SKU>>(new Map<number, SKU>());

  const [status, setStatus] = useImmer<{ loading: boolean; loaded: boolean; error: APIError }>({
    loading: false,
    loaded: false,
    error: null,
  });

  // 全部商品规格: 商品维度
  const [specificationMap, setSpecificationMap] = useImmer<{
    [goodsId: number]: Specification[];
  }>({} as const);

  // 全部商品规格: 规格维度
  const [skuSpecificationMap, setSkuSpecificationMap] = useImmer<{
    [specificationId: number]: SKUSpecification;
  }>({} as const);

  // 商品 {sn : id}
  const [goodsSnToGoodsIdMap, setGoodsSnToGoodsIdMap] = useImmer<Map<string, number>>(new Map<string, number>());

  // 品类 商品关系  [品类 id] : [商品 id]
  const [categoryIdGoodsIdMap, setCategoryIdGoodsIdMap] = useImmer<{
    [categoryId: number]: Set<number>;
  }>({});

  const getAllGoods = useCallback(async () => {
    const goodsList = await requestAllGoods();
    if (goodsList) {
      // 搜索引擎
      goodsSearchIndex.current.setCollection(goodsList.map(item => goodsToSearchDoc(item)));

      // 添加到类目
      setCategoryIdGoodsIdMap(draft => {
        goodsList.forEach(goods => {
          const categoryId = goods.categoryId;
          if (!draft[categoryId]) {
            draft[categoryId] = new Set();
          }
          draft[categoryId].add(goods.id);
        });
      });

      // 添加 sn -> id 映射
      setGoodsSnToGoodsIdMap(draft => {
        goodsList.forEach(goods => {
          if (goods.goodsSn !== undefined) {
            draft.set(goods.goodsSn, goods.id);
          }
        });
      });

      setGoodsMap(() => {
        return goodsList.reduce((acc, cur) => {
          // return Object.assign(acc, {
          //   [cur.id]: cur,
          // });
          acc.set(cur.id, cur);
          return acc;
        }, new Map<number, SKU>());
      });
      return goodsList;
      // 添加到搜索引擎
      // goodsSearchIndex.setCollection(goodsList.map(item => goodsToSearchDoc(item)));
    } else {
      throw new Error('没有商品数据');
    }
  }, [setCategoryIdGoodsIdMap, setGoodsSnToGoodsIdMap, setGoodsMap]);

  const getAllSpecifications = useCallback(async () => {
    const specifications = await requestAllSpecifications();
    if (specifications) {
      setSpecificationMap(draft => {
        // 严格模式兼容
        draft = {};
        specifications.forEach(specification => {
          const goodsId = specification.goodsId;
          if (!draft[goodsId]) {
            draft[goodsId] = [];
          }
          draft[goodsId].push(specification);
        });
        return draft;
      });

      setSkuSpecificationMap(() =>
        specifications.reduce((acc, cur) => {
          acc[cur.id] = cur;
          return acc;
        }, {})
      );

      return specifications;
    } else {
      throw new Error('没有规格数据');
    }
  }, [setSpecificationMap]);

  const fetchData = useCallback(
    async function () {
      setStatus(draft => {
        draft.loading = true;
        draft.loaded = false;
        draft.error = null;
      });
      try {
        await getAllGoods();
        await getAllSpecifications();

        setStatus(draft => {
          draft.loading = false;
          draft.loaded = true;
          draft.error = null;
        });
      } catch (e) {
        setStatus(draft => {
          draft.loading = false;
          draft.loaded = false;
          draft.error = { msg: '数据获取失败' };
        });
      }
    },
    [getAllSpecifications, getAllGoods]
  );

  /**
   * 查找 skuWrapper by 商品 id
   */
  const getSKUWrapperByGoodsId = useCallback(
    (goodsId: number) => {
      const goods = goodsMap.get(goodsId);
      if (
        // 未查询到
        !goods
      ) {
        return null;
      }
      const specifications = specificationMap[goodsId];
      if (!specifications || specifications.length < 1) {
        return null;
      }
      return {
        goods: goods,
        specifications: specifications,
      } as SKUWrapper;
    },
    [goodsMap, specificationMap]
  );

  /**
   * 根据条码查找商品
   */
  const getSkuWrapperByGoodsSn = useCallback(
    (sn: string) => {
      if (goodsSnToGoodsIdMap.has(sn)) {
        const goodsId = goodsSnToGoodsIdMap.get(sn);
        return getSKUWrapperByGoodsId(goodsId);
      }
      return null;
    },
    [goodsSnToGoodsIdMap, getSKUWrapperByGoodsId]
  );

  const updateStock = useCallback(
    (stockMap: { [goodsIdAndSpecificationId: string]: number }) => {
      setSpecificationMap(draft => {
        Object.entries(stockMap).forEach(([key, number]) => {
          const [goodsId, specificationId] = key.split('-');
          const specificationList = draft[+goodsId];
          const specificationIndex = specificationList.findIndex(
            specification => +specification.id === +specificationId
          );

          if (specificationIndex >= 0) {
            specificationList[specificationIndex].stock = number;
          }
        });
      });
    },
    [setSpecificationMap]
  );

  return {
    ...status,
    goodsMap,
    setGoodsMap,
    specificationMap,
    setSpecificationMap,
    goodsSnToGoodsIdMap,
    setGoodsSnToGoodsIdMap,
    categoryIdGoodsIdMap,
    setCategoryIdGoodsIdMap,
    // getAllGoods,
    // getAllSpecifications,
    getSKUWrapperByGoodsId,
    getSkuWrapperByGoodsSn,
    updateStock,
    fetchData,
    goodsSearchIndex: goodsSearchIndex.current,
    searchGoods,
    skuSpecificationMap,
  };
};

// export const useFindGoodsOrSpBySn = () => {
//   const { send, ...rest } = useRequestImmer<{ goods?: SKU; specifications: Specification[] }>(
//     {
//       url: `${skuEndpoint}/goodsOrSpBySn/{sn}`,
//     },
//     { manual: true }
//   );
//   const get = useCallback(
//     (sn: string) => {
//       return send({
//         pathVars: { sn },
//       });
//     },
//     [send]
//   );
//
//   return {
//     get,
//     ...rest,
//   };
// };

// export const requestAllGoodsMap = () => {
//   const { payload: goodsList, ...others } = useRequestImmer<SKU[]>(
//     {
//       method: 'get',
//       url: `${skuEndpoint}/getAll`,
//     },
//     {
//       manual: true,
//     }
//   );
//
//   const payload = useMemo(() => {
//     if (goodsList && goodsList.length) {
//       return goodsList.reduce(
//         (acc, cur) => {
//           return Object.assign(acc, {
//             [cur.id]: cur,
//           });
//         },
//         {} as {
//           [id: string]: SKU;
//         }
//       );
//     }
//   }, [goodsList]);
//
//   return {
//     ...others,
//     payload,
//   };
// };

export const getTotalDuratonBySKUSpecifications = (skuSpecification: SKUSpecification[]) => {
  return (skuSpecification || []).reduce((acc, cur) => cur.goodsDuration + acc, 0);
};
